import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import * as api from '../../api';
import * as FormUtils from '../../utils/form';
import * as store from '../../reducers/store';

import { startForm as startFormCallback } from '../../utils/form/index';
import { sendEventMessage, Message } from '../../utils/native';
import { setFormField } from '../../reducers/formField';
import { setDocuments, setFormSets, setFormStatus } from '../../reducers/root';
import {
  getReviewPath,
  getVisibleFields,
  onFormComplete,
} from '../../utils/form';
import * as Cache from '../../utils/cache';

export const useForm = ({
  formId,
  authenticationCode,
  redirectOnFormStart,
}: {
  formId: string;
  authenticationCode: string;
  redirectOnFormStart?: boolean;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { multiFormSet, formSets, formSet, patient, preferredLanguage } =
    useFormContext(formId);
  if (!formSet) {
    throw new Error(`Cannot find parent form set for formId=${formId}`);
  }

  const form = formSet.forms[formId];
  const collectFeedback = formSet.practicePreference.collectFeedback;

  const _onFormComplete = async () => {
    const incompleteReviewableForm = Object.entries(formSet.forms).find(
      ([, form]) =>
        form.isReviewable && form.status !== api.PrevisitForm.Status.Completed,
    );
    if (incompleteReviewableForm) {
      const [formId, form] = incompleteReviewableForm;
      history.push(
        getReviewPath({
          authenticationCode,
          formSetId: form.previsitFormSetId,
          formId: formId,
          multi: multiFormSet,
        }),
      );
    } else if (form.status === api.PrevisitForm.Status.Completed) {
      await onFormComplete({
        history,
        authenticationCode,
        multiFormSet,
        collectFeedback,
        formSets,
      });
    }
  };

  const startForm = async () => {
    await Cache.startPrevisitFormSet(formSet.id, authenticationCode);
    await startFormCallback({
      history,
      authenticationCode,
      formSetId: formSet.id,
      multiFormSet,
      redirect: redirectOnFormStart,
      form,
      setFormStatus: ({ formSetId, formId, status }) =>
        dispatch(setFormStatus({ formSetId, formId, status })),
    });
  };

  const completeForm = async () => {
    await FormUtils.maybeUpdateFormStatus(
      form.id,
      api.PrevisitForm.Status.Completed,
    );
    dispatch(
      setFormStatus({
        formSetId: formSet.id,
        formId: form.id,
        status: api.PrevisitForm.Status.Completed,
      }),
    );

    const { formSets } = await api.client.Previsit.fetchFormSets(
      authenticationCode,
      preferredLanguage || undefined,
      multiFormSet,
      true,
    );
    dispatch(
      setFormSets({
        formSets,
      }),
    );

    const { formSets: serverFormSets, documents: serverFormSetsDocuments } =
      await reloadFormSets();
    const serverFormSetForms = serverFormSets.find(
      (serverFormSet) => serverFormSet.previsitFormSet.id === formSet.id,
    )?.previsitFormSet.forms;
    const serverFormSetDocuments = serverFormSetsDocuments.find(
      (documents) => documents?.formSetId === formSet.id,
    )?.documents;
    if (
      !FormUtils.hasIncompleteForm(
        serverFormSetForms || {},
        serverFormSetDocuments || [],
      )
    ) {
      await api.client.Previsit.submitFormSet(formSet.id, authenticationCode);
    }

    await _onFormComplete();
  };

  const reloadFormSets = async () => {
    const { formSets } = await api.client.Previsit.fetchFormSets(
      authenticationCode,
      preferredLanguage || undefined,
      multiFormSet,
      true,
    );
    dispatch(
      setFormSets({
        formSets,
      }),
    );
    const maybeLoadDocuments = async (forms: store.Forms) => {
      const existingDocumentSigner =
        FormUtils.getMaybeDocumentSignerFormField(forms);
      if (!existingDocumentSigner) return;
      const documents =
        await api.client.PrevisitDocumentTemplate.findAllByFormSetAndField(
          existingDocumentSigner.previsitFormSetId,
          existingDocumentSigner.id,
        );
      dispatch(
        setDocuments({
          formSetId: existingDocumentSigner.previsitFormSetId,
          documents,
        }),
      );
      return { formSetId: existingDocumentSigner.previsitFormSetId, documents };
    };

    const documents = await Promise.all(
      formSets.map((formSet) =>
        maybeLoadDocuments(formSet.previsitFormSet.forms || {}),
      ),
    );
    return { formSets, documents };
  };

  const saveFieldValue = async (
    formFieldId: string,
    value: string,
  ): Promise<void> => {
    await api.client.Previsit.updateFormField(formSet.id, formFieldId, value);
    sendEventMessage(Message.ActiveAction);
  };

  return {
    form,
    formSet,
    startForm,
    completeForm,
    multiFormSet,
    saveFieldValue,
    setFieldValue: (formFieldId: string, value: string) =>
      dispatch(setFormField({ formFieldId, value })),
    formSets,
    patient,
    visibleFormFields: getVisibleFields({
      fields: form.fields,
      sourceFields: form.fields,
      patient,
    }),
  };
};

const useFormContext = (formId: string) => {
  const multiFormSet = useSelector((store: store.All) => store.multiFormSet);
  const formSets = useSelector((store: store.All) => store.formSets);
  const preferredLanguage = useSelector(
    (store: store.All) => store.preferredLanguage,
  );
  const formSet = formId
    ? Object.values(formSets).find((formSet) => formSet.forms[formId])
    : undefined;
  const patient = useSelector((store: store.All) => store.loggedInPatient);

  return {
    preferredLanguage,
    multiFormSet,
    formSets,
    formSet,
    patient,
  };
};
